// ---------------------------------------------------------------------------
// CLIENT-WIDE HINE / HAI HISTORY STACK
// ---------------------------------------------------------------------------
// The HINE / HAI screens used to inherit score rows from ONE source session
// (the nearest completed session). That made every appointment show only the
// previous single set of rows.
//
// Now the rows are stacked from EVERY session of the client (client-based,
// NO age filter), newest session first:
//
//   - the newest section is rendered in position A (the editable top row)
//   - every earlier section is rendered below it, one by one (B, C, D ...)
//
// ``Initial A+B -> RSG (edit A -> C) -> Review`` therefore displays:
//   A = C (the edit), B = [old A, old B]  ... and after another edit (D):
//   A = D, B = [C, A, B].
//
// A section keeps its identity by VALUE. When a Review copies the fetched rows
// forward without touching them, the copies dedupe back to their ORIGINAL
// session/position, so an untouched appointment adds no duplicate section and
// the "last entered" data is simply shown again (the optional/review case).
// When a row is edited its value changes, which makes it a NEW key and it
// becomes the newest section (the "edit A -> becomes C" rule).
// ---------------------------------------------------------------------------

const normTimepoint = (v) => String(v == null ? "" : v).trim().toLowerCase();
const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v));

const hineKey = (e) =>
  JSON.stringify([
    normTimepoint(e.timepoint),
    num(e.hine_score),
    num(e.right_asymmetry),
    num(e.left_asymmetry),
  ]);

const haiKey = (e) =>
  JSON.stringify([
    normTimepoint(e.timepoint),
    num(e.right_score),
    num(e.left_score),
    num(e.sum),
    num(e.asymmetry),
  ]);

// API entry object -> React row (same shapes the rest of start-assessment uses).
const toHineRow = (e) => ({
  id: Date.now() + Math.random(),
  timepoint: e.timepoint || "",
  hineScore: num(e.hine_score),
  rightAsym: num(e.right_asymmetry),
  leftAsym: num(e.left_asymmetry),
});

const toHaiRow = (e) => ({
  id: Date.now() + Math.random(),
  timepoint: e.timepoint || "",
  rightEahs: num(e.right_score),
  leftEahs: num(e.left_score),
  eachHandSumScore: num(e.sum),
  asymmetryIndex: num(e.asymmetry),
});

// React rows -> API entry objects (mirrors handleHineChange / handleHaiChange).
export const toHineEntries = (rows) =>
  (rows || []).map((r) => ({
    timepoint: r.timepoint,
    hine_score: num(r.hineScore),
    right_asymmetry: num(r.rightAsym),
    left_asymmetry: num(r.leftAsym),
  }));

export const toHaiEntries = (rows) =>
  (rows || []).map((r) => ({
    timepoint: r.timepoint,
    right_score: num(r.rightEahs),
    left_score: num(r.leftEahs),
    sum: num(r.eachHandSumScore),
    asymmetry: num(r.asymmetryIndex),
  }));

const collectStack = (sessions, currentSessionId, type) => {
  const currentId = String(currentSessionId ?? "");
  const seen = new Set();
  const firstSeen = []; // { sessionIndex, pos, entry } - creation order

  const typeEntries = (s) =>
    (s?.pathway || []).flatMap((q) => {
      if (String(q?.type || "").toUpperCase().trim() !== type) return [];
      const ans = q?.answer;
      if (!ans || typeof ans !== "object") return [];
      if (String(ans.value || "").toLowerCase() !== "yes") return [];
      return Array.isArray(ans.entries) ? ans.entries : [];
    });

  const keyOf = type === "HINE" ? hineKey : haiKey;

  // Server returns sessions oldest -> newest. Walk oldest first so the FIRST
  // occurrence of each section records its ORIGINAL position.
  (Array.isArray(sessions) ? sessions : []).forEach((s, sessionIndex) => {
    const sid = String(s?.session_id ?? s?.sessionId ?? "");
    if (sid && sid === currentId) return; // the session being answered is excluded
    typeEntries(s).forEach((e, pos) => {
      const key = keyOf(e);
      if (seen.has(key)) return; // copy of an already-placed section
      seen.add(key);
      firstSeen.push({ sessionIndex, pos, entry: e });
    });
  });

  // Newest creation session first; within a session keep the entered order
  // (row 0 = A is the newest of that session, the following rows are earlier).
  firstSeen.sort((a, b) => b.sessionIndex - a.sessionIndex || a.pos - b.pos);
  return firstSeen.map((x) => x.entry);
};

export const buildHineHaiStack = (sessions, currentSessionId) => ({
  hine: collectStack(sessions, currentSessionId, "HINE").map(toHineRow),
  hai: collectStack(sessions, currentSessionId, "HAI").map(toHaiRow),
});